import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import AccessDenied from "./AccessDenied";
import { base_url } from "../api/index";
import { printReceiptThermal } from "../api/pos/receiptPrint";
import { useDispatch, useSelector } from "react-redux";
import { usePermissions } from "../context/PermissionsContext";
import { fetchTableOrderStocks } from "../redux/stocksSlice";
import Modal from "../components/HesabKesModal";
import AmountCalculator from "./AmountCalculator";
import { useI18n } from "../i18n/I18nContext";

const getHeaders = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

function HesapKes({ orderStocks, orderId, totalAmount, prepaidAmount = 0 }) {
  const { id } = useParams();
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { t } = useI18n();

  const [isCariMusteriSelected, setIsCariMusteriSelected] = useState(false);
  const [isParcaParcaOde, setIsParcaParcaOde] = useState(false);
  const [numberOfPeople, setNumberOfPeople] = useState(2);
  const [discount, setDiscount] = useState("");
  const [sum, setSum] = useState(Array(numberOfPeople).fill(0));
  const [selectedPaymentType, setSelectedPaymentType] = useState("");
  const [customerOptions, setCustomerOptions] = useState<any[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [showCashModal, setShowCashModal] = useState(false);
  const [alinanMebleg, setAlinanMebleg] = useState("");
  const [accessDenied, setAccessDenied] = useState(false);

  const { allItems, orders } = useSelector((state: any) => state.stocks);

  // orderId prop Modal-dan OBYEKT ({id, total_price, total_prepayment}) gələ bilər,
  // başqa yollardan düz string id. Real sifariş id-sini həmişə STRİNGƏ çevir (boş → '')
  // — əks halda obyekt birbaşa render olunsa React çökür ("Objects are not valid as
  // a React child").
  const resolvedOrderId =
    orders?.[0]?.order_id ||
    (orderId && typeof orderId === "object" ? (orderId as any).id : orderId) ||
    "";

  // ✅ Qəbz üçün print ref
  const printRef = useRef(null);
  const [isPrinting, setIsPrinting] = useState(false);

  // ✅ Endirim + Ön ödəniş düşülmüş yekun
  const discountedTotalRaw = totalAmount * (1 - parseFloat(discount || '0') / 100);
  const discountedTotal = Math.max(
    0,
    Number((discountedTotalRaw - Number(prepaidAmount || 0)).toFixed(2))
  );

  // 🟡 Qalıq məbləğ (nağd modal üçün)
  const qaliqMebleg =
    alinanMebleg !== "" ? Number((parseFloat(alinanMebleg || '0') - discountedTotal).toFixed(2)) : null;

  useEffect(() => {
    dispatch(fetchTableOrderStocks(id as any));
  }, [id, dispatch]);

  useEffect(() => {
    // `/customers` cloud endpoint-dir və `manage-customers` icazəsi tələb edir.
    // İcazə yoxdursa çağırma (403 spam-ının qarşısını alır). 401/403 sakitcə udulur.
    if (isCariMusteriSelected && hasPermission("manage-customers")) {
      axios
        .get(`${base_url}/customers`, getHeaders())
        .then((res) => setCustomerOptions(res.data))
        .catch((err) => {
          const s = err?.response?.status;
          if (s !== 401 && s !== 403) console.error("Error fetching customers:", err);
        });
    }
  }, [isCariMusteriSelected, hasPermission]);

  useEffect(() => {
    if (isParcaParcaOde) {
      updateSumArray(numberOfPeople);
    }
  }, [discount, numberOfPeople, isParcaParcaOde]); // eslint-disable-line

  // ✅ Ödəniş tipi seçimi
  const handlePaymentTypeChange = (type) => {
    setSelectedPaymentType(type);
    setIsCariMusteriSelected(type === "musteriye-aktar");
    setIsParcaParcaOde(type === "parca-ode");
    if (type === "parca-ode") {
      setNumberOfPeople(2);
      updateSumArray(2);
    } else {
      // hissə-hissə deyilsə, bölünməyi sıfırla
      setNumberOfPeople(0);
      setSum([]);
    }
    if (type === "pesin") {
      setShowCashModal(true); // nağd seçiləndə modal aç
    }
  };

  // ✅ Hissə-hissə bölünmə
  const updateSumArray = (peopleCount) => {
    const base = Math.floor((discountedTotal / peopleCount) * 100) / 100;
    const arr = new Array(peopleCount).fill(base);
    const distributed = base * peopleCount;
    if (distributed < discountedTotal) {
      arr[peopleCount - 1] = Number((arr[peopleCount - 1] + (discountedTotal - distributed)).toFixed(2));
    }
    setSum(arr);
  };

  const handleNumberOfPeopleChange = (newNumber) => {
    setNumberOfPeople(newNumber);
    updateSumArray(newNumber);
  };

  const handleSumChange = (index, value) => {
    const newSum = [...sum];
    newSum[index] = parseFloat(value) || 0;
    setSum(newSum);
  };

  const totalSum = sum.reduce((acc, curr) => acc + (Number(curr) || 0), 0);
  const sumMessage =
    isParcaParcaOde && totalSum !== discountedTotal
      ? totalSum > discountedTotal
        ? t("checkout.msg.exceeded")
        : t("checkout.msg.short", { n: Math.abs(discountedTotal - totalSum).toFixed(2) })
      : "";

  // ✅ Çap – sadə print (mövcud layoutdan)
  const handlePrint = async () => {
    try {
      setIsPrinting(true);
      const finalAmount = Number(totalAmount || 0) - Number(prepaidAmount || 0);
      if (isNaN(finalAmount)) {
        alert(t("checkout.alert.invalidData"));
        return;
      }

      // POS rejimi: qəbz birbaşa termal çek aparatından çıxır, brauzer açılmır.
      if (await printReceiptThermal(resolvedOrderId)) return;

      // Sadə print: yalnız qəbz hissəsini çap etmək üçün:
      if (printRef.current) {
        const printContents = printRef.current.innerHTML;
        const win = window.open("", "PRINT", "height=650,width=900,top=100,left=150");
        win.document.write(`
          <html>
            <head>
              <title>${t("checkout.receipt")}</title>
              <style>
                body { font-family: sans-serif; padding: 16px; }
                .row { display: flex; justify-content: space-between; margin: 6px 0; }
                .muted { color: #6b7280; font-size: 12px; }
                .title { font-size: 16px; font-weight: 700; margin-bottom: 10px; }
                .hr { border-top: 1px dashed #e5e7eb; margin: 10px 0; }
              </style>
            </head>
            <body>${printContents}</body>
          </html>
        `);
        win.document.close();
        win.focus();
        win.print();
        win.close();
      } else {
        // Bütün səhifəni çap et
        window.print();
      }
    } catch (err: any) {
      console.error("Qəbz çapında xəta:", err);
      alert(err?.message || t("checkout.alert.printError"));
    } finally {
      setIsPrinting(false);
    }
  };

  // ✅ Ödənişi göndər
  const handleSubmit = async (event, forceSubmit = false) => {
    if (event?.preventDefault) event.preventDefault();

    if (discountedTotal <= 0) {
      alert(t("checkout.alert.discountZero"));
      return;
    }

    if (selectedPaymentType === "parca-ode" && sum.some((amount) => amount <= 0)) {
      alert(t("checkout.alert.validAmounts"));
      return;
    }

    if (selectedPaymentType === "musteriye-aktar" && !selectedCustomerId) {
      alert(t("checkout.alert.selectCustomer"));
      return;
    }

    const mappedItems = (allItems || []).map((item) => ({
      name: item.name,
      quantity: item.quantity,
      price: item.price,
    }));

    const paymentData = {
      discount: parseFloat(discount) || 0,
      received_amount: parseFloat(alinanMebleg) || 0,
      remaining_amount: qaliqMebleg !== null ? parseFloat(qaliqMebleg.toFixed(2)) : 0,
      total_amount: parseFloat(Number(totalAmount || 0).toFixed(2)),
      discounted_total: parseFloat(Number(discountedTotal || 0).toFixed(2)),
      prepaid_amount: parseFloat(Number(prepaidAmount || 0).toFixed(2)), // ✅ ön ödəniş
      shares: [],
    };

    if (selectedPaymentType === "parca-ode") {
      paymentData.shares = sum.map((amount) => ({
        type: "cash",
        amount: parseFloat(amount),
        customer_id: null,
        items: mappedItems,
      }));
    } else {
      paymentData.shares.push({
        type:
          selectedPaymentType === "pesin"
            ? "cash"
            : selectedPaymentType === "bank-havale"
            ? "bank"
            : "customer_balance",
        amount: parseFloat(Number(discountedTotal || 0).toFixed(2)),
        customer_id: selectedCustomerId || null,
        items: mappedItems,
      });
    }

    // 🛑 Nağd ödəniş zamanı məbləğ yoxlaması (forceSubmit = bypass)
    if (!forceSubmit && selectedPaymentType === "pesin" && parseFloat(alinanMebleg || '0') < discountedTotal) {
      alert(t("checkout.alert.insufficient"));
      return;
    }

    try {
      await axios.post(
        `${base_url}/order/${resolvedOrderId}/payments`,
        paymentData,
        getHeaders()
      );
      alert(t("checkout.alert.success"));
      navigate("/masalar");
      window.location.reload();
    } catch (error: any) {
      if (error?.response?.status === 403 && error?.response?.data?.message === "Forbidden") {
        setAccessDenied(true);
      } else {
        console.error("Error submitting payment:", error);
        alert(t("checkout.alert.submitError"));
      }
    }
  };

  if (accessDenied) return <AccessDenied onClose={setAccessDenied} />;

  return (
    <>
      {/* ✅ PRINT SAHƏSİ – yalnız qəbz üçün (istəyə görə zənginləşdir) */}
      <div ref={printRef} style={{ position: "absolute", left: -99999, top: -99999 }}>
        <div className="title">{t("checkout.receipt")}</div>
        <div className="row"><span className="muted">{t("checkout.order")}</span><span>{resolvedOrderId}</span></div>
        <div className="row"><span className="muted">{t("checkout.total")}</span><span>{Number(totalAmount || 0).toFixed(2)} ₼</span></div>
        <div className="row"><span className="muted">{t("checkout.discountLabel")}</span><span>{Number(discount || 0)} %</span></div>
        <div className="row"><span className="muted">{t("checkout.prepayLabel")}</span><span>{Number(prepaidAmount || 0).toFixed(2)} ₼</span></div>
        <div className="row"><span className="muted">{t("checkout.payableLabel")}</span><span>{Number(discountedTotal || 0).toFixed(2)} ₼</span></div>
        <div className="hr"></div>
        <div className="muted">{new Date().toLocaleString()}</div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Toplam məbləğ */}
        <div className="border rounded bg-gray-50 m-4 p-3">
          <div className="flex items-center">
            <div className="w-1/3 flex h-14 border rounded-l items-center px-2 bg-gray-100 gap-5">
              {t("checkout.totalAmount")}
            </div>
            <input
              className="w-2/3 h-14 px-6 border border-l-0 rounded-r"
              type="text"
              value={Number(totalAmount || 0).toFixed(2)}
              readOnly
            />
          </div>
        </div>

        {/* Artıq ödənilib */}
        <div className="border rounded bg-green-50 m-4 p-3">
          <div className="flex items-center">
            <div className="w-1/3 flex h-14 border rounded-l items-center px-2 bg-gray-100 gap-5">
              {t("checkout.alreadyPaidPrepay")}
            </div>
            <input
              className="w-2/3 h-14 px-6 border border-l-0 rounded-r"
              type="text"
              value={Number(prepaidAmount || orderId?.total_prepayment || 0).toFixed(2)}
              readOnly
            />
          </div>
        </div>

        {/* İndirim və Qalıq (vizual sahə) */}
        <div className="border rounded bg-gray-50 m-4 p-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[220px] flex h-14 border rounded items-center px-2 bg-gray-100">
              {t("checkout.discountPct")}
            </div>
            <input
              className="flex-1 min-w-[220px] h-14 px-6 border rounded"
              type="number"
              min="0"
              max="100"
              step="1"
              value={discount}
              onChange={(e) => setDiscount(e.target.value === "" ? "" : String(Math.min(100, Math.max(0, Number(e.target.value)))))}
            />

            <div className="flex-1 min-w-[220px] flex h-14 border rounded items-center px-2 bg-gray-100">
              {t("checkout.payableFull")}
            </div>
            <input
              className="flex-1 min-w-[220px] h-14 px-6 border rounded"
              type="text"
              value={Number(discountedTotal || 0).toFixed(2)}
              readOnly
            />
          </div>
        </div>

        {/* Ödəniş tipi */}
        <div className="mx-4 flex flex-col gap-2">
          {["pesin", "bank-havale", "musteriye-aktar", "parca-ode"].map((type) => (
            <label
              key={type}
              className={`flex items-center p-2 border rounded bg-white shadow-sm ${
                selectedPaymentType === type ? "bg-yellow-100 border-yellow-500" : ""
              }`}
            >
              <input
                type="radio"
                name="odemeType"
                id={type}
                checked={selectedPaymentType === type}
                onChange={() => handlePaymentTypeChange(type)}
                className="mr-2"
              />
              {type === "pesin" && t("checkout.pay.cash")}
              {type === "bank-havale" && t("checkout.pay.bank")}
              {type === "musteriye-aktar" && t("checkout.pay.customer")}
              {type === "parca-ode" && t("checkout.pay.split")}
            </label>
          ))}
        </div>

        {/* Cari müştəri seçimi */}
        {isCariMusteriSelected && (
          <div id="aktar" className="p-4 bg-white shadow-md rounded-lg mt-4">
            <p className="text-lg font-semibold mb-2">{t("checkout.customers")}</p>
            <select
              className="form-select block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              onChange={(e) => setSelectedCustomerId(e.target.value)}
              value={selectedCustomerId || ""}
            >
              <option value="">{t("checkout.select")}</option>
              {customerOptions.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Hissə-hissə ödəniş */}
        {isParcaParcaOde && (
          <div id="parcaode" className="p-4 bg-white shadow-md rounded-lg mt-4">
            <div className="flex flex-wrap gap-2 mb-4">
              {[2, 3, 4, 5].map((num) => (
                <div
                  key={num}
                  onClick={() => handleNumberOfPeopleChange(num)}
                  className={`flex-1 min-w-[100px] p-4 border border-gray-300 rounded-lg bg-gray-50 text-center cursor-pointer ${
                    numberOfPeople === num ? "bg-yellow-100" : ""
                  }`}
                >
                  {t("checkout.people", { n: num })}
                </div>
              ))}
            </div>

            <div className="mb-4">
              <div className="grid grid-cols-3 gap-4 text-center font-semibold border-b border-gray-300 pb-2 mb-2">
                <div>{t("checkout.col.no")}</div>
                <div>{t("checkout.col.amount")}</div>
                <div>{t("checkout.col.payment")}</div>
              </div>
              <div>
                {sum.map((_, index) => (
                  <div key={index} className="grid grid-cols-3 gap-4 items-center border-b border-gray-200 py-2">
                    <div className="flex items-center justify-center border border-gray-300 p-2 rounded">
                      {index + 1}.
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      value={sum[index]}
                      onChange={(e) => handleSumChange(index, e.target.value)}
                      className="border border-gray-300 rounded p-2 w-full"
                    />
                    <select className="border border-gray-300 rounded p-2 w-full">
                      <option>{t("checkout.opt.cash")}</option>
                      <option>{t("checkout.opt.bank")}</option>
                    </select>
                  </div>
                ))}
              </div>
            </div>

            {sumMessage && (
              <div className={`p-2 mt-4 text-white font-semibold ${totalSum > discountedTotal ? "bg-red-600" : "bg-yellow-600"}`}>
                {sumMessage}
              </div>
            )}
          </div>
        )}

        {/* Hesab kəs (submit) */}
        <button
          type="submit"
          className="block w-[calc(100%-32px)] bg-sky-600 font-medium mx-4 mb-3 py-2 px-4 rounded text-white hover:bg-sky-700 transition"
        >
          {t("checkout.submit")}
        </button>

        {/* Qəbz Çap Et */}
        <button
          type="button"
          onClick={handlePrint}
          disabled={isPrinting}
          className="block w-[calc(100%-32px)] bg-green-600 font-medium mx-4 mb-6 py-2 px-4 rounded text-white hover:bg-green-700 transition disabled:opacity-50"
        >
          {isPrinting ? t("checkout.printing") : t("checkout.print")}
        </button>
      </form>

      {/* Nağd modal */}
      <Modal isOpen={showCashModal} onClose={() => setShowCashModal(false)}>
        <h2 className="text-lg font-bold mb-4">{t("checkout.cashPayment")}</h2>

        <div className="mb-3">
          <div className="text-sm text-gray-600 mb-1">{t("checkout.grandTotalLabel")}</div>
          <div className="px-3 py-2 border rounded bg-gray-100 text-lg font-semibold text-red-600">
            {Number(discountedTotal || 0).toFixed(2)} ₼
          </div>
        </div>

        <div className="mb-4">
          <AmountCalculator value={alinanMebleg} onChange={setAlinanMebleg} />
        </div>

        {qaliqMebleg !== null && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">{t("checkout.change")}:</label>
            <div className={`px-3 py-2 border rounded ${qaliqMebleg < 0 ? "text-red-600" : "text-green-600"}`}>
              {qaliqMebleg.toFixed(2)} ₼
              {qaliqMebleg < 0 ? ` ${t("checkout.shortAmount")}` : ` ${t("checkout.willReturn")}`}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <button className="px-4 py-2 bg-gray-300 rounded" onClick={() => setShowCashModal(false)}>
            {t("common.cancel")}
          </button>

          {/* ✅ Sadəcə Hesab Kəs (məbləğ yoxlaması olmadan) */}
          <button
            type="button"
            className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
            // əvvəl səhv idi: disabled {alinanMebleg && parseFloat(alinanMebleg) > 0}
            // indi həmişə aktiv, çünki "sadəcə hesab kəs" üçün məbləğ şərti yoxdur
            onClick={() => {
              setShowCashModal(false);
              handleSubmit(null, true); // ✅ məbləğ yoxlamasını bypass edir
            }}
          >
            {t("checkout.justCheckout")}
          </button>

          {/* ✅ Təsdiqlə (nağd alinanMebleg yoxlanır) */}
          <button
            type="button"
            className="px-4 py-2 bg-green-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!alinanMebleg || parseFloat(alinanMebleg || '0') < discountedTotal}
            onClick={(e) => {
              setShowCashModal(false);
              handleSubmit(e, false);
            }}
          >
            {t("checkout.confirm")}
          </button>
        </div>
      </Modal>
    </>
  );
}

export default HesapKes;


